import * as React from 'react'
import { ClientSideError, ClientSideErrorType, logger } from '@dtx-company/logger'
import { LockModalGraphic, LockModalGraphics } from '../../constants/lockModal'
import {
  LockModalProPlusVariants,
  LockModalProVariants,
  LockModalVariants
} from '@dtx-company/inter-app/src/constants/lockModal'
import { NOT_BILLING_ADMIN } from '@app/common/src/components/PlanPurchaseExperience/constants'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { RefObject, useMemo, useState } from 'react'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import {
  setLockModalVariant,
  useLockModalState
} from '@dtx-company/inter-app/src/redux/slices/lockModalSlice'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useDispatch } from 'react-redux'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'
import { useIsBillingAdmin } from '@dtx-company/inter-app/src/hooks/useIsBillingAdmin/useIsBillingAdmin'
import { usePaymentMethodError } from '@app/common/src/components/PlanPurchaseExperience/usePaymentMethodError'

interface UseLockModalReturnType {
  open: boolean
  onClose: () => void
  lockModalVariant?: LockModalVariants
  lockPlanType: PlanType
  graphic: LockModalGraphic | undefined
}

export const useLockModal = (ref?: RefObject<HTMLDivElement>): UseLockModalReturnType => {
  const router = useFlowRouter()
  const dispatch = useDispatch()
  const { lockModalVariant } = useLockModalState()
  const [lockPlanType, setLockPlanType] = React.useState<PlanType>(PlanType.FREE)
  const [open, setOpen] = useState(false)
  const isProLock = Object.values(LockModalProVariants).includes(
    lockModalVariant as LockModalProVariants
  )
  const isProPlusLock = Object.values(LockModalProPlusVariants).includes(
    lockModalVariant as LockModalProPlusVariants
  )
  const isBillingAdmin = useIsBillingAdmin()
  const { getErrorMessage } = usePaymentMethodError()

  // Setup modal and open it
  React.useEffect(() => {
    if (!lockModalVariant) return
    if (!isBillingAdmin) {
      const error = new ClientSideError({
        name: NOT_BILLING_ADMIN,
        message: NOT_BILLING_ADMIN,
        type: ClientSideErrorType.ClientSideValidationError
      })
      logger.logError(error, { team: 'monetization', technicalArea: 'monetization' })
      sendErrorNotification(getErrorMessage(error))
      dispatch(setLockModalVariant())
      return
    }
    if (isProLock) setLockPlanType(PlanType.PRO)
    if (isProPlusLock) setLockPlanType(PlanType.PRO_PLUS_ORG)

    // Must delay opening modal until all values are set so that it has the proper initial
    // values for reporting to heap
    // https://app.shortcut.com/flowcode/story/79061
    setOpen(true)
  }, [
    lockModalVariant,
    router,
    isProLock,
    isProPlusLock,
    isBillingAdmin,
    dispatch,
    getErrorMessage
  ])

  const onClose = (): void => {
    dispatch(setLockModalVariant())
    setOpen(false)
    const { promoCode: _promoCode, ...query } = router.query
    const updateAddressBar = (): void => {
      router.replace(
        {
          query,
          // The flowpage editor uses hashes to navigate between tabs and we do not wanna loose those
          // after the users closes the lock modal
          hash: window.location.hash
        },
        undefined,
        { scroll: false } // Prevent scrolling up to the top when the modal is closed
      )
    }
    // It seems that sometimes doing the `router.replace` while the dialog close transition
    // runs that it gets interrupted and never unmounts from the DOM, leaving an invisible
    // but still interactive layer above everything
    // The close transition is 225ms so calling this once immediately since that generally
    // works and the delay can be disruptive and then again after 225ms to be safe
    // https://app.shortcut.com/flowcode/story/79061
    if (!ref) {
      updateAddressBar()
    } else {
      const checkRef = (): void => {
        if (!ref || !ref.current) {
          updateAddressBar()
        } else {
          window.requestAnimationFrame(checkRef)
        }
      }
      checkRef()
    }
  }

  const graphic = useMemo(() => {
    if (lockModalVariant) return LockModalGraphics[lockModalVariant]
    else return undefined
  }, [lockModalVariant])

  return { open, onClose, lockModalVariant, lockPlanType, graphic }
}

export const useLockModalCodeLimit = (): { lockModalVariant: LockModalVariants } => {
  const { hasOperation } = useAuthState()
  const lockModalVariant = hasOperation('view:pro_plus_upsell_modal')
    ? LockModalVariants.FC_PRO_PLUS_CODE_LIMIT
    : LockModalVariants.FC_PRO_CODE_LIMIT

  return { lockModalVariant }
}
